import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { db } from '../lib/firebase';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  addDoc,
} from 'firebase/firestore';

/* ─── Styled components ─── */

const Wrap = styled.div`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid var(--border);
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--text-primary);
  margin: 0 0 1.5rem;
`;

/* ── Form box ── */

const FormBox = styled.div`
  background: var(--bg-secondary);
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const FormTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 1rem;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
  @media (min-width: 640px) { grid-template-columns: 1fr 1fr; }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const Label = styled.label`
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-secondary);
  span { color: #ef4444; }
`;

const inputStyles = `
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.375rem;
  background: var(--card-bg);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: inherit;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.15s;
  &:focus { border-color: var(--link); }
`;

const Input = styled.input`${inputStyles}`;

const Textarea = styled.textarea`
  ${inputStyles}
  resize: vertical;
  min-height: 100px;
`;

const CharCount = styled.div`
  font-size: 0.7rem;
  color: var(--text-muted);
  text-align: right;
  margin-top: 0.2rem;
`;

const AlertBox = styled.div`
  padding: 0.6rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  background: ${p => p.type === 'error' ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)'};
  border: 1px solid ${p => p.type === 'error' ? 'rgba(239,68,68,0.4)' : 'rgba(34,197,94,0.4)'};
  color: ${p => p.type === 'error' ? '#dc2626' : '#16a34a'};
`;

const SubmitBtn = styled.button`
  padding: 0.6rem 1.5rem;
  background: var(--link);
  color: #ffffff;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;
  &:hover { background: var(--link-hover); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

/* ── Comment list ── */

const CommentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CommentCard = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  padding: 1rem 1.25rem;
`;

const CommentMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.6rem;
`;

const Avatar = styled.div`
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 9999px;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 0.875rem;
  flex-shrink: 0;
`;

const MetaText = styled.div``;

const CommentAuthor = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const CommentTime = styled.div`
  font-size: 0.7rem;
  color: var(--text-muted);
`;

const CommentBody = styled.p`
  font-size: 0.9rem;
  color: var(--text-secondary);
  line-height: 1.6;
  white-space: pre-wrap;
  margin: 0;
`;

const EmptyMsg = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-muted);
  font-size: 0.9rem;
`;

const LoadingMsg = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-muted);
  font-size: 0.9rem;
`;

/* ─── Helpers ─── */

const timeAgo = (dateStr) => {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/* ─── Component ─── */

const CommentsSection = ({ articleId }) => {
  const [comments, setComments]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');
  const [form, setForm]             = useState({ name: '', email: '', content: '' });

  useEffect(() => {
    fetchComments();
  }, [articleId]);

  const fetchComments = async () => {
    try {
      const q = query(
        collection(db, 'comments'),
        where('articleId', '==', articleId),
        where('approved', '==', true),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      setComments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Failed to load comments', e);
      setComments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.name.trim() || form.name.trim().length < 2) {
      setError('Name must be at least 2 characters.');
      return;
    }
    if (!form.content.trim() || form.content.trim().length < 3) {
      setError('Comment must be at least 3 characters.');
      return;
    }
    if (form.content.trim().length > 1000) {
      setError('Comment cannot exceed 1000 characters.');
      return;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    try {
      await addDoc(collection(db, 'comments'), {
        articleId,
        name:      form.name.trim(),
        email:     form.email.trim() || null,
        content:   form.content.trim(),
        approved:  true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      setSuccess('Comment posted!');
      setForm({ name: '', email: '', content: '' });
      fetchComments();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      console.error('Failed to post comment', e);
      setError('Failed to post comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Wrap>
      <SectionTitle>Comments ({loading ? '…' : comments.length})</SectionTitle>

      {/* ── Form ── */}
      <FormBox>
        <FormTitle>Leave a Comment</FormTitle>
        <form onSubmit={handleSubmit}>
          <FieldRow>
            <Field>
              <Label htmlFor="cc-name">Name <span>*</span></Label>
              <Input
                id="cc-name"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Your name"
                maxLength={50}
                required
              />
            </Field>
            <Field>
              <Label htmlFor="cc-email">Email <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></Label>
              <Input
                id="cc-email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="your@email.com"
              />
            </Field>
          </FieldRow>

          <Field style={{ marginBottom: '1rem' }}>
            <Label htmlFor="cc-content">Comment <span>*</span></Label>
            <Textarea
              id="cc-content"
              name="content"
              value={form.content}
              onChange={handleChange}
              placeholder="Share your thoughts…"
              maxLength={1000}
              required
            />
            <CharCount>{form.content.length}/1000</CharCount>
          </Field>

          {error   && <AlertBox type="error">{error}</AlertBox>}
          {success && <AlertBox type="success">{success}</AlertBox>}

          <SubmitBtn type="submit" disabled={submitting}>
            {submitting ? 'Posting…' : 'Post Comment'}
          </SubmitBtn>
        </form>
      </FormBox>

      {/* ── Comments list ── */}
      {loading ? (
        <LoadingMsg>Loading comments…</LoadingMsg>
      ) : comments.length === 0 ? (
        <EmptyMsg>No comments yet. Be the first to comment!</EmptyMsg>
      ) : (
        <CommentsList>
          {comments.map(c => (
            <CommentCard key={c.id}>
              <CommentMeta>
                <Avatar>{c.name.charAt(0).toUpperCase()}</Avatar>
                <MetaText>
                  <CommentAuthor>{c.name}</CommentAuthor>
                  <CommentTime>{timeAgo(c.createdAt)}</CommentTime>
                </MetaText>
              </CommentMeta>
              <CommentBody>{c.content}</CommentBody>
            </CommentCard>
          ))}
        </CommentsList>
      )}
    </Wrap>
  );
};

export default CommentsSection;
